<template>
	<v-layout row justify-center v-if="hasContent">
		<v-dialog v-model="dialog" scrollable max-width="1140px" content-class="faq-dialog">
			<template v-slot:activator="{ on }">
				<a v-on="on" id="showFaq" @click="$gtag('send', 'event', 'header', 'home', 'FAQ')">
					<Icon :name="'uil uil-comment-question'" :size="24" />
				</a>
			</template>
			<v-card>
				<v-card-title class="headline">
					<h1>
						<div>Perguntas Frequentes</div>
					</h1>
					<a href="#"></a>
					<v-btn class="close-modal" @click="dialog = false">
						<i class="fal fa-times"></i>
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<div class="faq faq--tree">
						<div class="faq-layout" v-if="filteredTree.length">
							<!-- Menu lateral de categorias -->
							<aside class="faq-sidebar">
								<ul class="faq-menu">
									<li
										v-for="cat in filteredTree"
										:key="cat.id"
										class="faq-menu__item"
										:class="{ 'faq-menu__item--active': currentCategory && cat.id === currentCategory.id }"
										@click="selectCategory(cat.id)"
									>
										<span class="faq-menu__name">{{ cat.name }}</span>
									</li>
								</ul>
							</aside>

							<!-- Painel de conteúdo da categoria selecionada -->
							<section class="faq-content" v-if="currentCategory">
								<div class="faq-search">
									<InputSearch type="text" place-holder="Buscar..." v-model="search" />
								</div>
								<h3 class="faq-content__title">{{ currentCategory.name }}</h3>
								<div v-for="sub in currentCategory.subcategories" :key="sub.id" class="faq-sub">
									<div class="faq-sub__label text-primary color-theme-primaria" v-html="sub.name"></div>
									<ul>
										<li v-for="faq in sub.faqs" :key="faq.source + '_' + faq.id">
											<input type="checkbox" :checked="accordion" :id="questionId(currentCategory, sub, faq)" />
											<label :for="questionId(currentCategory, sub, faq)" v-html="faq.question"></label>
											<div class="faq-answer" v-html="faq.answer"></div>
										</li>
									</ul>
								</div>
							</section>
						</div>

						<div v-else class="faq-content">
							<div class="faq-search">
								<InputSearch type="text" place-holder="Buscar..." v-model="search" />
							</div>
							<div class="faq-empty">
								<template v-if="search">Nenhuma pergunta encontrada para "{{ search }}".</template>
								<template v-else>Nenhuma pergunta disponível no momento.</template>
							</div>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-layout>
</template>

<script type="plain/text">
import Campaign from "@/scripts/models/campaign.model";
import CampaignService from "@/scripts/services/campaign.service";
import FaqService from "@/scripts/services/faq.service";

export default {
	data() {
		return {
			search: "",
			accordion: false,
			campaign: new Campaign(),
			campaignService: new CampaignService(),
			faqService: new FaqService(),
			tree: [],
			selectedCategoryId: null,
			dialog: false
		};
	},
	created() {
		this.findBySubdomain();
	},
	computed: {
		hasContent() {
			return this.totalQuestionsInTree(this.tree) > 0;
		},
		// Categoria exibida no painel: a selecionada ou, na ausência, a primeira disponível.
		currentCategory() {
			if (this.filteredTree.length === 0) return null;
			return (
				this.filteredTree.find(cat => cat.id === this.selectedCategoryId) ||
				this.filteredTree[0]
			);
		},
		filteredTree() {
			const term = this.search.trim().toLowerCase();

			if (!term) {
				// Sem busca: árvore inteira, mantendo só categorias/subcategorias com perguntas.
				// Personalizadas (Campaign) primeiro, depois as da base (KnowledgeBase).
				return this.tree
					.map(cat => this.mapCategory(cat, sub => sub.faqs.slice().sort(this.sortFaqs)))
					.filter(Boolean);
			}

			const re = new RegExp("(" + this.escapeRegExp(this.search.trim()) + ")", "gi");

			return this.tree
				.map(cat => {
					const subcategories = (cat.subcategories || [])
						.map(sub => {
							// Se o título da subcategoria casa com a busca, traz todas as perguntas dela.
							const subMatches = (sub.name || "").toLowerCase().includes(term);
							const matchedFaqs = subMatches
								? sub.faqs
								: sub.faqs.filter(faq =>
									(faq.question || "").toLowerCase().includes(term) ||
									(faq.answer || "").toLowerCase().includes(term)
								);
							const faqs = matchedFaqs
								.map(faq => ({
									...faq,
									question: this.highlight(faq.question, re),
									answer: this.highlight(faq.answer, re)
								}))
								.sort(this.sortFaqs);

							return { ...sub, name: this.highlight(sub.name, re), faqs };
						})
						.filter(sub => sub.faqs.length > 0);

					return subcategories.length > 0 ? { ...cat, subcategories } : null;
				})
				.filter(Boolean);
		}
	},
	watch: {
		search(value) {
			// Ao buscar, abre as respostas automaticamente para facilitar a leitura.
			this.accordion = value.trim() !== "";
		}
	},
	methods: {
		// Personalizadas (Campaign) primeiro, na ordem da campanha; depois as da base (KnowledgeBase).
		sortFaqs(a, b) {
			const aGroup = a.source === "Campaign" ? 0 : 1;
			const bGroup = b.source === "Campaign" ? 0 : 1;
			if (aGroup !== bGroup) return aGroup - bGroup;
			const ao = a.sortOrder == null ? Infinity : a.sortOrder;
			const bo = b.sortOrder == null ? Infinity : b.sortOrder;
			if (ao !== bo) return ao - bo;
			return (a.id || 0) - (b.id || 0);
		},
		findBySubdomain() {
			this.campaignService.findBySubdomain(this.findBySubdomainCallback);
		},
		findBySubdomainCallback(data) {
			if (data != undefined) this.campaign = data;
			this.loadTree();
		},
		loadTree() {
			this.faqService.listTree(this.loadTreeCallback, {
				campaignId: this.campaign && this.campaign.id ? this.campaign.id : undefined,
				visible: true
			});
		},
		loadTreeCallback(data) {
			this.tree = Array.isArray(data) ? data : [];
		},
		// Mantém só subcategorias com perguntas (após o seletor) e descarta categoria vazia.
		mapCategory(cat, faqSelector) {
			const subcategories = (cat.subcategories || [])
				.map(sub => ({ ...sub, faqs: faqSelector(sub) || [] }))
				.filter(sub => sub.faqs.length > 0);

			if (subcategories.length === 0) return null;
			return { ...cat, subcategories };
		},
		countCategory(cat) {
			return (cat.subcategories || []).reduce((sum, sub) => sum + (sub.faqs ? sub.faqs.length : 0), 0);
		},
		totalQuestionsInTree(tree) {
			return (tree || []).reduce((sum, cat) => sum + this.countCategory(cat), 0);
		},
		questionId(cat, sub, faq) {
			return `faq_${cat.id}_${sub.id}_${faq.source}_${faq.id}`;
		},
		selectCategory(catId) {
			this.selectedCategoryId = catId;
		},
		escapeRegExp(text) {
			return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		},
		// Aplica o destaque de busca sem quebrar tags HTML da resposta.
		highlight(text, re) {
			if (!text) return text;
			return text
				.split(/(<[^>]+>)/g)
				.map(part =>
					part.startsWith("<") && part.endsWith(">")
						? part
						: part.replace(re, `<b class="faq-search-text">$1</b>`)
				)
				.join("");
		}
	}
};
</script>
